import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { languages } from '../i18n';

interface LanguageMenuProps {
  /** In the bar it shrinks to a flag on a phone; inside the menu that bar collapses into there is room for all of it. */
  variant?: 'bar' | 'menu';
}

/** Driven from React rather than Bootstrap's dropdown JavaScript - the classes are Bootstrap's, the state is ours. */
export function LanguageMenu({ variant = 'bar' }: LanguageMenuProps = {}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  const current = languages.find((language) => language.locale === i18n.resolvedLanguage) ?? languages[0];

  useEffect(() => {
    if (!open) {
      return;
    }

    function onPointerDown(event: MouseEvent) {
      if (!container.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // Bootstrap's responsive utilities hide the label below sm, which is right in the bar and wrong in the
  // menu: a class rather than a style, so the two cases stay one component.
  const label = variant === 'menu' ? '' : 'd-none d-sm-inline';

  return (
    <div className={`dropdown language-menu language-menu--${variant}`} ref={container}>
      <button
        type="button"
        className="btn btn-light dropdown-toggle d-flex align-items-center gap-2"
        aria-expanded={open}
        aria-label={t('nav.language')}
        onClick={() => setOpen((value) => !value)}
      >
        <img src={current.flag} alt="" className="country-flag" />
        <span className={label}>{current.label}</span>
        {/* The chevron is what the label makes sense of; on a phone, where the label is hidden, it is 12
            pixels of the actions row saying nothing. */}
        <i className={`pi pi-angle-down ${label}`} aria-hidden="true" />
      </button>

      <ul className={`dropdown-menu dropdown-menu-end${open ? ' show' : ''}`}>
        {languages.map((language) => (
          <li key={language.locale}>
            <button
              type="button"
              className={`dropdown-item d-flex align-items-center gap-2${
                language.locale === current.locale ? ' active' : ''
              }`}
              onClick={() => {
                void i18n.changeLanguage(language.locale);
                setOpen(false);
              }}
            >
              <img src={language.flag} alt="" className="country-flag" />
              {language.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
